import { useEffect, useState } from "react";

// UI chrome themes — dialogs, menus, menu bar, context menus. Chosen under
// File > Theme; Acrylic Glass (June 2026) is the default and is unchanged.
//
// Every theme is a full token set; applying one writes each token to a
// `--ui-*` CSS custom property on <html>, so the global stylesheet and the
// inline styles built from `theme/acrylic.ts` (whose values are `var(--ui-*)`
// references) re-skin live with no per-dialog work. HMI widgets are NOT themed
// here — their colours come from each config's Mviews.ini.
//
// The choice persists in the user's Tiger prefs (`user_profiles/{user}_prefs.ini`,
// via the backend) and is mirrored in localStorage so every window can apply it
// synchronously before first paint. Tool windows (graph, data grid, diagnostics)
// are separate documents on the same origin, so they read the mirror at startup
// and follow live switches through the `storage` event — identically under Tauri
// and in the browser.

export type UiThemeId =
  | "acrylic-glass"
  | "chromalloy-dark"
  | "chromalloy-light"
  | "classic-desktop"
  | "cupertino-light"
  | "fluent-light"
  | "high-contrast"
  | "industrial-dark"
  | "material-you"
  | "nord-twilight"
  | "steel-enterprise";

export interface UiThemeTokens {
  font: string;
  colorScheme: "light" | "dark";
  bgPrimary: string;
  bgSecondary: string;
  overlayDim: string;
  surface: string;
  surfaceBlur: string;
  surfaceBorder: string;
  surfaceRadius: string;
  surfaceShadow: string;
  titleBg: string;
  titleText: string;
  titleBorder: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  accent: string;
  accentHover: string;
  accentText: string;
  card: string;
  cardBorder: string;
  cardRadius: string;
  controlBg: string;
  controlBorder: string;
  controlRadius: string;
  controlHoverBg: string;
  tableBg: string;
  tableBorder: string;
  tableHeaderBg: string;
  tableHeaderText: string;
  rowDivider: string;
  selection: string;
  selectionText: string;
  rowHover: string;
  footerBg: string;
  footerBorder: string;
  separator: string;
  menuBg: string;
  menuBorder: string;
  menuShadow: string;
  menuBarHover: string;
  menuBarHoverText: string;
  btnPrimaryBg: string;
  btnPrimaryHoverBg: string;
  btnPrimaryText: string;
  btnPrimaryBorder: string;
  btnPrimaryShadow: string;
  btnSecondaryBg: string;
  btnSecondaryHoverBg: string;
  btnSecondaryText: string;
  btnSecondaryBorder: string;
  buttonActive: string;
  groupLabelBg: string;
  treeToggle: string;
  panelBg: string;
  // Tool-window chrome (graph / data grid / diagnostics toolbars and buttons)
  // and the graph plot area (canvas background, axis text, grid lines).
  toolbarBg: string;
  toolbarBorder: string;
  toolBtnBg: string;
  toolBtnBorder: string;
  toolBtnText: string;
  toolBtnActiveBg: string;
  toolBtnActiveBorder: string;
  toolBtnActiveText: string;
  plotBg: string;
  plotBorder: string;
  plotAxis: string;
  plotGrid: string;
}

export interface UiTheme {
  id: UiThemeId;
  label: string;
  tokens: UiThemeTokens;
}

export const UI_THEME_STORAGE_KEY = "tiger-ui-theme";
export const UI_THEME_EVENT = "tiger-ui-theme";
export const DEFAULT_UI_THEME: UiThemeId = "acrylic-glass";

const SEGOE = "'Segoe UI Variable', 'Segoe UI', sans-serif";

const acrylicGlass: UiThemeTokens = {
  font: SEGOE,
  colorScheme: "light",
  bgPrimary: "#f0f0f0",
  bgSecondary: "#ffffff",
  overlayDim: "rgba(30,40,60,0.25)",
  surface: "rgba(252,253,255,0.82)",
  surfaceBlur: "blur(14px)",
  surfaceBorder: "1px solid rgba(255,255,255,0.65)",
  surfaceRadius: "14px",
  surfaceShadow: "0 16px 48px rgba(20,30,60,0.35)",
  titleBg: "transparent",
  titleText: "#1a1d24",
  titleBorder: "none",
  textPrimary: "#1a1d24",
  textSecondary: "#4b5260",
  textMuted: "#8a8f99",
  accent: "#4c6ef5",
  accentHover: "#4263eb",
  accentText: "#ffffff",
  card: "rgba(255,255,255,0.55)",
  cardBorder: "1px solid rgba(255,255,255,0.8)",
  cardRadius: "10px",
  controlBg: "rgba(255,255,255,0.85)",
  controlBorder: "1px solid rgba(120,130,150,0.35)",
  controlRadius: "7px",
  controlHoverBg: "rgba(120,130,150,0.15)",
  tableBg: "rgba(255,255,255,0.7)",
  tableBorder: "1px solid rgba(160,170,190,0.3)",
  tableHeaderBg: "rgba(244,246,251,0.95)",
  tableHeaderText: "#4b5260",
  rowDivider: "1px solid rgba(160,170,190,0.18)",
  selection: "rgba(76,110,245,0.85)",
  selectionText: "#ffffff",
  rowHover: "rgba(76,110,245,0.08)",
  footerBg: "rgba(255,255,255,0.45)",
  footerBorder: "1px solid rgba(255,255,255,0.7)",
  separator: "rgba(120,130,150,0.25)",
  menuBg: "#fcfdff",
  menuBorder: "1px solid rgba(120,130,150,0.25)",
  menuShadow: "0 12px 36px rgba(20,30,60,0.22)",
  menuBarHover: "rgba(76,110,245,0.14)",
  menuBarHoverText: "#3b5bdb",
  btnPrimaryBg: "linear-gradient(180deg, #5b7cfa, #4c6ef5)",
  btnPrimaryHoverBg: "linear-gradient(180deg, #4c6ef5, #4263eb)",
  btnPrimaryText: "#ffffff",
  btnPrimaryBorder: "none",
  btnPrimaryShadow: "0 2px 6px rgba(76,110,245,0.4)",
  btnSecondaryBg: "rgba(255,255,255,0.8)",
  btnSecondaryHoverBg: "#ffffff",
  btnSecondaryText: "#3c4250",
  btnSecondaryBorder: "1px solid rgba(120,130,150,0.35)",
  buttonActive: "rgba(76,110,245,0.12)",
  groupLabelBg: "rgba(252,253,255,0.95)",
  treeToggle: "#64748b",
  panelBg: "rgba(248,249,251,0.96)",
  toolbarBg: "#f0f0f0",
  toolbarBorder: "#c0c0c0",
  toolBtnBg: "#e8e8e8",
  toolBtnBorder: "#c0c0c0",
  toolBtnText: "#333333",
  toolBtnActiveBg: "#dbeafe",
  toolBtnActiveBorder: "#0078d4",
  toolBtnActiveText: "#0078d4",
  plotBg: "#ffffff",
  plotBorder: "#999999",
  plotAxis: "#333333",
  plotGrid: "#e0e0e0",
};

// SCADA / control-room: dark graphite, amber accent (mockup B).
const industrialDark: UiThemeTokens = {
  font: SEGOE,
  colorScheme: "dark",
  bgPrimary: "#0d1117",
  bgSecondary: "#1c2127",
  overlayDim: "rgba(0,0,0,0.55)",
  surface: "#1c2127",
  surfaceBlur: "none",
  surfaceBorder: "1px solid #343b45",
  surfaceRadius: "10px",
  surfaceShadow: "0 12px 32px rgba(0,0,0,0.6)",
  titleBg: "#161a1f",
  titleText: "#e6e9ed",
  titleBorder: "1px solid #343b45",
  textPrimary: "#e6e9ed",
  textSecondary: "#9aa4b0",
  textMuted: "#6b7480",
  accent: "#f5a623",
  accentHover: "#e0961c",
  accentText: "#161a1f",
  card: "#242a32",
  cardBorder: "1px solid #343b45",
  cardRadius: "6px",
  controlBg: "#161a1f",
  controlBorder: "1px solid #3a424d",
  controlRadius: "4px",
  controlHoverBg: "rgba(255,255,255,0.08)",
  tableBg: "#1c2127",
  tableBorder: "1px solid #343b45",
  tableHeaderBg: "#161a1f",
  tableHeaderText: "#9aa4b0",
  rowDivider: "1px solid #2a313a",
  selection: "#f5a623",
  selectionText: "#161a1f",
  rowHover: "rgba(245,166,35,0.12)",
  footerBg: "#161a1f",
  footerBorder: "1px solid #343b45",
  separator: "#343b45",
  menuBg: "#1c2127",
  menuBorder: "1px solid #343b45",
  menuShadow: "0 12px 36px rgba(0,0,0,0.6)",
  menuBarHover: "rgba(245,166,35,0.18)",
  menuBarHoverText: "#f5a623",
  btnPrimaryBg: "#f5a623",
  btnPrimaryHoverBg: "#e0961c",
  btnPrimaryText: "#161a1f",
  btnPrimaryBorder: "none",
  btnPrimaryShadow: "none",
  btnSecondaryBg: "#1c2127",
  btnSecondaryHoverBg: "#242a32",
  btnSecondaryText: "#e6e9ed",
  btnSecondaryBorder: "1px solid #3a424d",
  buttonActive: "rgba(245,166,35,0.25)",
  groupLabelBg: "#1c2127",
  treeToggle: "#6b7480",
  panelBg: "#161a1f",
  toolbarBg: "#161a1f",
  toolbarBorder: "#343b45",
  toolBtnBg: "#242a32",
  toolBtnBorder: "#3a424d",
  toolBtnText: "#e6e9ed",
  toolBtnActiveBg: "rgba(245,166,35,0.2)",
  toolBtnActiveBorder: "#f5a623",
  toolBtnActiveText: "#f5a623",
  plotBg: "#0d1117",
  plotBorder: "#343b45",
  plotAxis: "#9aa4b0",
  plotGrid: "#2a313a",
};

// Faithful to the original MFC TigerUI (mockup C).
const classicDesktop: UiThemeTokens = {
  font: "Tahoma, 'MS Shell Dlg 2', sans-serif",
  colorScheme: "light",
  bgPrimary: "#f0f0f0",
  bgSecondary: "#ffffff",
  overlayDim: "rgba(0,0,0,0.2)",
  surface: "#f0f0f0",
  surfaceBlur: "none",
  surfaceBorder: "1px solid #6b7c93",
  surfaceRadius: "0px",
  surfaceShadow: "2px 2px 6px rgba(0,0,0,0.4)",
  titleBg: "linear-gradient(90deg, #0a246a, #a6caf0)",
  titleText: "#ffffff",
  titleBorder: "none",
  textPrimary: "#000000",
  textSecondary: "#333333",
  textMuted: "#808080",
  accent: "#0b61ff",
  accentHover: "#0a4fd0",
  accentText: "#ffffff",
  card: "transparent",
  cardBorder: "1px groove #d4d0c8",
  cardRadius: "0px",
  controlBg: "#ffffff",
  controlBorder: "1px solid #7a7a7a",
  controlRadius: "0px",
  controlHoverBg: "#e1e1e1",
  tableBg: "#ffffff",
  tableBorder: "1px inset #d4d0c8",
  tableHeaderBg: "#d6d3ce",
  tableHeaderText: "#000000",
  rowDivider: "1px solid #e5e5e5",
  selection: "#0b61ff",
  selectionText: "#ffffff",
  rowHover: "rgba(11,97,255,0.08)",
  footerBg: "#f0f0f0",
  footerBorder: "1px solid #d4d0c8",
  separator: "#adadad",
  menuBg: "#f0f0f0",
  menuBorder: "1px solid #adadad",
  menuShadow: "2px 2px 6px rgba(0,0,0,0.3)",
  menuBarHover: "#e1e1e1",
  menuBarHoverText: "#000000",
  btnPrimaryBg: "#e1e1e1",
  btnPrimaryHoverBg: "#e5f1fb",
  btnPrimaryText: "#000000",
  btnPrimaryBorder: "1px solid #0b61ff",
  btnPrimaryShadow: "none",
  btnSecondaryBg: "#e1e1e1",
  btnSecondaryHoverBg: "#e5f1fb",
  btnSecondaryText: "#000000",
  btnSecondaryBorder: "1px solid #adadad",
  buttonActive: "#cce4f7",
  groupLabelBg: "#f0f0f0",
  treeToggle: "#555555",
  panelBg: "#f0f0f0",
  toolbarBg: "#f0f0f0",
  toolbarBorder: "#adadad",
  toolBtnBg: "#e1e1e1",
  toolBtnBorder: "#adadad",
  toolBtnText: "#000000",
  toolBtnActiveBg: "#cce4f7",
  toolBtnActiveBorder: "#0b61ff",
  toolBtnActiveText: "#000000",
  plotBg: "#ffffff",
  plotBorder: "#7a7a7a",
  plotAxis: "#000000",
  plotGrid: "#d4d0c8",
};

// Soft Scandinavian dark: blue-grey surfaces, frost-cyan highlights (mockup E).
const nordTwilight: UiThemeTokens = {
  font: SEGOE,
  colorScheme: "dark",
  bgPrimary: "#242933",
  bgSecondary: "#2e3440",
  overlayDim: "rgba(0,0,0,0.45)",
  surface: "#2e3440",
  surfaceBlur: "none",
  surfaceBorder: "1px solid #434c5e",
  surfaceRadius: "8px",
  surfaceShadow: "0 12px 32px rgba(0,0,0,0.5)",
  titleBg: "#3b4252",
  titleText: "#eceff4",
  titleBorder: "1px solid #434c5e",
  textPrimary: "#d8dee9",
  textSecondary: "#9aa5b8",
  textMuted: "#7b88a1",
  accent: "#88c0d0",
  accentHover: "#7ab3c4",
  accentText: "#2e3440",
  card: "#3b4252",
  cardBorder: "1px solid transparent",
  cardRadius: "6px",
  controlBg: "#2e3440",
  controlBorder: "1px solid #4c566a",
  controlRadius: "4px",
  controlHoverBg: "rgba(255,255,255,0.08)",
  tableBg: "#2e3440",
  tableBorder: "1px solid #434c5e",
  tableHeaderBg: "#3b4252",
  tableHeaderText: "#9aa5b8",
  rowDivider: "1px solid #3b4252",
  selection: "#88c0d0",
  selectionText: "#2e3440",
  rowHover: "rgba(136,192,208,0.12)",
  footerBg: "#3b4252",
  footerBorder: "1px solid #434c5e",
  separator: "#434c5e",
  menuBg: "#2e3440",
  menuBorder: "1px solid #434c5e",
  menuShadow: "0 12px 36px rgba(0,0,0,0.5)",
  menuBarHover: "rgba(136,192,208,0.18)",
  menuBarHoverText: "#88c0d0",
  btnPrimaryBg: "#5e81ac",
  btnPrimaryHoverBg: "#6a8fbd",
  btnPrimaryText: "#eceff4",
  btnPrimaryBorder: "none",
  btnPrimaryShadow: "none",
  btnSecondaryBg: "#2e3440",
  btnSecondaryHoverBg: "#3b4252",
  btnSecondaryText: "#d8dee9",
  btnSecondaryBorder: "1px solid #4c566a",
  buttonActive: "rgba(136,192,208,0.25)",
  groupLabelBg: "#2e3440",
  treeToggle: "#7b88a1",
  panelBg: "#3b4252",
  toolbarBg: "#3b4252",
  toolbarBorder: "#434c5e",
  toolBtnBg: "#434c5e",
  toolBtnBorder: "#4c566a",
  toolBtnText: "#d8dee9",
  toolBtnActiveBg: "rgba(136,192,208,0.25)",
  toolBtnActiveBorder: "#88c0d0",
  toolBtnActiveText: "#88c0d0",
  plotBg: "#2e3440",
  plotBorder: "#434c5e",
  plotAxis: "#d8dee9",
  plotGrid: "#3b4252",
};

// WCAG-AAA: pure black and white, 2px borders, gold labels and selection (mockup P).
const highContrast: UiThemeTokens = {
  font: SEGOE,
  colorScheme: "dark",
  bgPrimary: "#000000",
  bgSecondary: "#000000",
  overlayDim: "rgba(0,0,0,0.7)",
  surface: "#000000",
  surfaceBlur: "none",
  surfaceBorder: "2px solid #ffffff",
  surfaceRadius: "0px",
  surfaceShadow: "none",
  titleBg: "#000000",
  titleText: "#ffffff",
  titleBorder: "2px solid #ffffff",
  textPrimary: "#ffffff",
  textSecondary: "#ffd700",
  textMuted: "#9d9d9d",
  accent: "#ffd700",
  accentHover: "#ffe34d",
  accentText: "#000000",
  card: "transparent",
  cardBorder: "1px solid #ffffff",
  cardRadius: "0px",
  controlBg: "#000000",
  controlBorder: "2px solid #ffffff",
  controlRadius: "0px",
  controlHoverBg: "#333333",
  tableBg: "#000000",
  tableBorder: "2px solid #ffffff",
  tableHeaderBg: "#000000",
  tableHeaderText: "#ffffff",
  rowDivider: "1px solid #6e6e6e",
  selection: "#ffd700",
  selectionText: "#000000",
  rowHover: "rgba(255,215,0,0.2)",
  footerBg: "#000000",
  footerBorder: "2px solid #ffffff",
  separator: "#ffffff",
  menuBg: "#000000",
  menuBorder: "2px solid #ffffff",
  menuShadow: "none",
  menuBarHover: "#ffd700",
  menuBarHoverText: "#000000",
  btnPrimaryBg: "#ffffff",
  btnPrimaryHoverBg: "#ffd700",
  btnPrimaryText: "#000000",
  btnPrimaryBorder: "2px solid #ffffff",
  btnPrimaryShadow: "none",
  btnSecondaryBg: "#000000",
  btnSecondaryHoverBg: "#333333",
  btnSecondaryText: "#ffffff",
  btnSecondaryBorder: "2px solid #ffffff",
  buttonActive: "#ffd700",
  groupLabelBg: "#000000",
  treeToggle: "#ffffff",
  panelBg: "#000000",
  toolbarBg: "#000000",
  toolbarBorder: "#ffffff",
  toolBtnBg: "#000000",
  toolBtnBorder: "#ffffff",
  toolBtnText: "#ffffff",
  toolBtnActiveBg: "#ffd700",
  toolBtnActiveBorder: "#ffd700",
  toolBtnActiveText: "#000000",
  plotBg: "#000000",
  plotBorder: "#ffffff",
  plotAxis: "#ffffff",
  plotGrid: "#6e6e6e",
};

// Fluent Light: white surfaces, soft grey cards, Microsoft blue (mockup A).
const fluentLight: UiThemeTokens = {
  font: SEGOE,
  colorScheme: "light",
  bgPrimary: "#f3f4f6",
  bgSecondary: "#ffffff",
  overlayDim: "rgba(0,0,0,0.25)",
  surface: "#ffffff",
  surfaceBlur: "none",
  surfaceBorder: "1px solid #e5e7eb",
  surfaceRadius: "12px",
  surfaceShadow: "0 12px 32px rgba(0,0,0,0.18)",
  titleBg: "transparent",
  titleText: "#111827",
  titleBorder: "1px solid #e5e7eb",
  textPrimary: "#111827",
  textSecondary: "#374151",
  textMuted: "#9ca3af",
  accent: "#0078d4",
  accentHover: "#106ebe",
  accentText: "#ffffff",
  card: "#f9fafb",
  cardBorder: "1px solid #f3f4f6",
  cardRadius: "8px",
  controlBg: "#ffffff",
  controlBorder: "1px solid #d1d5db",
  controlRadius: "6px",
  controlHoverBg: "#f3f4f6",
  tableBg: "#ffffff",
  tableBorder: "1px solid #e5e7eb",
  tableHeaderBg: "#f9fafb",
  tableHeaderText: "#374151",
  rowDivider: "1px solid #f3f4f6",
  selection: "#0078d4",
  selectionText: "#ffffff",
  rowHover: "rgba(0,120,212,0.08)",
  footerBg: "#f9fafb",
  footerBorder: "1px solid #e5e7eb",
  separator: "#e5e7eb",
  menuBg: "#ffffff",
  menuBorder: "1px solid #e5e7eb",
  menuShadow: "0 12px 36px rgba(0,0,0,0.15)",
  menuBarHover: "rgba(0,120,212,0.12)",
  menuBarHoverText: "#0078d4",
  btnPrimaryBg: "#0078d4",
  btnPrimaryHoverBg: "#106ebe",
  btnPrimaryText: "#ffffff",
  btnPrimaryBorder: "none",
  btnPrimaryShadow: "none",
  btnSecondaryBg: "#ffffff",
  btnSecondaryHoverBg: "#f3f4f6",
  btnSecondaryText: "#374151",
  btnSecondaryBorder: "1px solid #d1d5db",
  buttonActive: "rgba(0,120,212,0.15)",
  groupLabelBg: "#ffffff",
  treeToggle: "#6b7280",
  panelBg: "#f9fafb",
  toolbarBg: "#f3f4f6",
  toolbarBorder: "#d1d5db",
  toolBtnBg: "#ffffff",
  toolBtnBorder: "#d1d5db",
  toolBtnText: "#374151",
  toolBtnActiveBg: "#e6f0fb",
  toolBtnActiveBorder: "#0078d4",
  toolBtnActiveText: "#0078d4",
  plotBg: "#ffffff",
  plotBorder: "#d1d5db",
  plotAxis: "#374151",
  plotGrid: "#e5e7eb",
};

// Steel Enterprise: flat, squared, data-dense, IBM Carbon inspired (mockup D).
const steelEnterprise: UiThemeTokens = {
  font: "'IBM Plex Sans', 'Segoe UI', sans-serif",
  colorScheme: "light",
  bgPrimary: "#e0e0e0",
  bgSecondary: "#f4f4f4",
  overlayDim: "rgba(22,22,22,0.5)",
  surface: "#f4f4f4",
  surfaceBlur: "none",
  surfaceBorder: "1px solid #c6c6c6",
  surfaceRadius: "0px",
  surfaceShadow: "0 8px 24px rgba(0,0,0,0.25)",
  titleBg: "#0f3a5d",
  titleText: "#ffffff",
  titleBorder: "none",
  textPrimary: "#161616",
  textSecondary: "#525252",
  textMuted: "#a8a8a8",
  accent: "#0f62fe",
  accentHover: "#0353e9",
  accentText: "#ffffff",
  card: "#ffffff",
  cardBorder: "1px solid #e0e0e0",
  cardRadius: "0px",
  controlBg: "#ffffff",
  controlBorder: "1px solid #8d8d8d",
  controlRadius: "0px",
  controlHoverBg: "#e5e5e5",
  tableBg: "#ffffff",
  tableBorder: "1px solid #e0e0e0",
  tableHeaderBg: "#e0e0e0",
  tableHeaderText: "#161616",
  rowDivider: "1px solid #f0f0f0",
  selection: "#0f62fe",
  selectionText: "#ffffff",
  rowHover: "#d0e2ff",
  footerBg: "#ffffff",
  footerBorder: "1px solid #e0e0e0",
  separator: "#c6c6c6",
  menuBg: "#f4f4f4",
  menuBorder: "1px solid #c6c6c6",
  menuShadow: "0 8px 24px rgba(0,0,0,0.25)",
  menuBarHover: "#d0e2ff",
  menuBarHoverText: "#0f62fe",
  btnPrimaryBg: "#0f62fe",
  btnPrimaryHoverBg: "#0353e9",
  btnPrimaryText: "#ffffff",
  btnPrimaryBorder: "none",
  btnPrimaryShadow: "none",
  btnSecondaryBg: "#ffffff",
  btnSecondaryHoverBg: "#e5e5e5",
  btnSecondaryText: "#161616",
  btnSecondaryBorder: "1px solid #8d8d8d",
  buttonActive: "#a6c8ff",
  groupLabelBg: "#f4f4f4",
  treeToggle: "#525252",
  panelBg: "#ffffff",
  toolbarBg: "#e0e0e0",
  toolbarBorder: "#c6c6c6",
  toolBtnBg: "#f4f4f4",
  toolBtnBorder: "#8d8d8d",
  toolBtnText: "#161616",
  toolBtnActiveBg: "#d0e2ff",
  toolBtnActiveBorder: "#0f62fe",
  toolBtnActiveText: "#0f62fe",
  plotBg: "#ffffff",
  plotBorder: "#8d8d8d",
  plotAxis: "#161616",
  plotGrid: "#e0e0e0",
};

// Material You: lavender tonal surfaces, very round cards (mockup J).
const materialYou: UiThemeTokens = {
  font: "Roboto, 'Segoe UI', sans-serif",
  colorScheme: "light",
  bgPrimary: "#e7e0ec",
  bgSecondary: "#fef7ff",
  overlayDim: "rgba(29,27,32,0.32)",
  surface: "#fef7ff",
  surfaceBlur: "none",
  surfaceBorder: "1px solid transparent",
  surfaceRadius: "20px",
  surfaceShadow: "0 8px 28px rgba(80,60,120,0.25)",
  titleBg: "transparent",
  titleText: "#1d1b20",
  titleBorder: "none",
  textPrimary: "#1d1b20",
  textSecondary: "#49454f",
  textMuted: "#79747e",
  accent: "#6750a4",
  accentHover: "#563d8f",
  accentText: "#ffffff",
  card: "#f3edf7",
  cardBorder: "1px solid transparent",
  cardRadius: "14px",
  controlBg: "#e8def8",
  controlBorder: "1px solid #cac4d0",
  controlRadius: "8px",
  controlHoverBg: "#e8def8",
  tableBg: "#ffffff",
  tableBorder: "1px solid #e7e0ec",
  tableHeaderBg: "#f3edf7",
  tableHeaderText: "#49454f",
  rowDivider: "1px solid #f3edf7",
  selection: "#e8def8",
  selectionText: "#21005d",
  rowHover: "rgba(103,80,164,0.08)",
  footerBg: "#f3edf7",
  footerBorder: "1px solid transparent",
  separator: "#e7e0ec",
  menuBg: "#fef7ff",
  menuBorder: "1px solid #e7e0ec",
  menuShadow: "0 8px 28px rgba(80,60,120,0.25)",
  menuBarHover: "#e8def8",
  menuBarHoverText: "#6750a4",
  btnPrimaryBg: "#6750a4",
  btnPrimaryHoverBg: "#563d8f",
  btnPrimaryText: "#ffffff",
  btnPrimaryBorder: "none",
  btnPrimaryShadow: "0 2px 6px rgba(103,80,164,0.35)",
  btnSecondaryBg: "#fef7ff",
  btnSecondaryHoverBg: "#f3edf7",
  btnSecondaryText: "#6750a4",
  btnSecondaryBorder: "1px solid #79747e",
  buttonActive: "#d0bcff",
  groupLabelBg: "#fef7ff",
  treeToggle: "#79747e",
  panelBg: "#f3edf7",
  toolbarBg: "#f3edf7",
  toolbarBorder: "#e7e0ec",
  toolBtnBg: "#fef7ff",
  toolBtnBorder: "#cac4d0",
  toolBtnText: "#49454f",
  toolBtnActiveBg: "#e8def8",
  toolBtnActiveBorder: "#6750a4",
  toolBtnActiveText: "#6750a4",
  plotBg: "#ffffff",
  plotBorder: "#cac4d0",
  plotAxis: "#49454f",
  plotGrid: "#e7e0ec",
};

// Cupertino Light: macOS refinement, hairline borders, system blue (mockup N).
const cupertinoLight: UiThemeTokens = {
  font: "-apple-system, 'Helvetica Neue', 'Segoe UI', sans-serif",
  colorScheme: "light",
  bgPrimary: "#e8e8ed",
  bgSecondary: "#ffffff",
  overlayDim: "rgba(0,0,0,0.25)",
  surface: "#ffffff",
  surfaceBlur: "none",
  surfaceBorder: "1px solid rgba(0,0,0,0.1)",
  surfaceRadius: "10px",
  surfaceShadow: "0 10px 36px rgba(0,0,0,0.22), 0 0 0 0.5px rgba(0,0,0,0.1)",
  titleBg: "linear-gradient(180deg, #fafafa, #f0f0f2)",
  titleText: "#3a3a3c",
  titleBorder: "1px solid #e3e3e6",
  textPrimary: "#1d1d1f",
  textSecondary: "#6e6e73",
  textMuted: "#aeaeb2",
  accent: "#007aff",
  accentHover: "#0062cc",
  accentText: "#ffffff",
  card: "#f5f5f7",
  cardBorder: "1px solid transparent",
  cardRadius: "8px",
  controlBg: "#ffffff",
  controlBorder: "1px solid #d2d2d7",
  controlRadius: "6px",
  controlHoverBg: "#f5f5f7",
  tableBg: "#ffffff",
  tableBorder: "1px solid #e3e3e6",
  tableHeaderBg: "#fafafa",
  tableHeaderText: "#6e6e73",
  rowDivider: "1px solid #e3e3e6",
  selection: "#007aff",
  selectionText: "#ffffff",
  rowHover: "#f5f5f7",
  footerBg: "#f5f5f7",
  footerBorder: "1px solid #e3e3e6",
  separator: "#e3e3e6",
  menuBg: "#ffffff",
  menuBorder: "1px solid #d2d2d7",
  menuShadow: "0 10px 36px rgba(0,0,0,0.22)",
  menuBarHover: "rgba(0,122,255,0.12)",
  menuBarHoverText: "#007aff",
  btnPrimaryBg: "linear-gradient(180deg, #2e8bff, #007aff)",
  btnPrimaryHoverBg: "#007aff",
  btnPrimaryText: "#ffffff",
  btnPrimaryBorder: "none",
  btnPrimaryShadow: "0 1px 3px rgba(0,122,255,0.4)",
  btnSecondaryBg: "#ffffff",
  btnSecondaryHoverBg: "#f5f5f7",
  btnSecondaryText: "#1d1d1f",
  btnSecondaryBorder: "1px solid #d2d2d7",
  buttonActive: "rgba(0,122,255,0.15)",
  groupLabelBg: "#ffffff",
  treeToggle: "#aeaeb2",
  panelBg: "#fafafa",
  toolbarBg: "#f5f5f7",
  toolbarBorder: "#e3e3e6",
  toolBtnBg: "#ffffff",
  toolBtnBorder: "#d2d2d7",
  toolBtnText: "#1d1d1f",
  toolBtnActiveBg: "#e5f1ff",
  toolBtnActiveBorder: "#007aff",
  toolBtnActiveText: "#007aff",
  plotBg: "#ffffff",
  plotBorder: "#d2d2d7",
  plotAxis: "#3a3a3c",
  plotGrid: "#e3e3e6",
};

// Chromalloy Light: brand cyan accent, slate title band, white body (chromalloy.com palette).
const chromalloyLight: UiThemeTokens = {
  font: "'Open Sans', 'Segoe UI Variable', 'Segoe UI', sans-serif",
  colorScheme: "light",
  bgPrimary: "#f9f9fb",
  bgSecondary: "#ffffff",
  overlayDim: "rgba(33,41,52,0.4)",
  surface: "#ffffff",
  surfaceBlur: "none",
  surfaceBorder: "1px solid #e6e6e6",
  surfaceRadius: "8px",
  surfaceShadow: "0 14px 40px rgba(0,0,0,0.25)",
  titleBg: "#212934",
  titleText: "#ffffff",
  titleBorder: "3px solid #00b8f1",
  textPrimary: "#333333",
  textSecondary: "#4a4e57",
  textMuted: "#797979",
  accent: "#00b8f1",
  accentHover: "#0091c0",
  accentText: "#212934",
  card: "#f9f9fb",
  cardBorder: "1px solid #e6e6e6",
  cardRadius: "6px",
  controlBg: "#ffffff",
  controlBorder: "1px solid #cfd3d8",
  controlRadius: "4px",
  controlHoverBg: "#f0f1f3",
  tableBg: "#ffffff",
  tableBorder: "1px solid #e6e6e6",
  tableHeaderBg: "#273a43",
  tableHeaderText: "#d7dde2",
  rowDivider: "1px solid #f0f1f3",
  selection: "#00b8f1",
  selectionText: "#212934",
  rowHover: "rgba(0,184,241,0.10)",
  footerBg: "#f9f9fb",
  footerBorder: "1px solid #e6e6e6",
  separator: "#e2e2e2",
  menuBg: "#ffffff",
  menuBorder: "1px solid #e6e6e6",
  menuShadow: "0 12px 30px rgba(33,41,52,0.18)",
  menuBarHover: "rgba(0,184,241,0.18)",
  menuBarHoverText: "#212934",
  btnPrimaryBg: "#00b8f1",
  btnPrimaryHoverBg: "#0091c0",
  btnPrimaryText: "#212934",
  btnPrimaryBorder: "none",
  btnPrimaryShadow: "none",
  btnSecondaryBg: "#ffffff",
  btnSecondaryHoverBg: "#f0f1f3",
  btnSecondaryText: "#4a4e57",
  btnSecondaryBorder: "1px solid #cfd3d8",
  buttonActive: "rgba(0,184,241,0.25)",
  groupLabelBg: "#ffffff",
  treeToggle: "#797979",
  panelBg: "#f9f9fb",
  toolbarBg: "#ffffff",
  toolbarBorder: "#e2e2e2",
  toolBtnBg: "#f9f9fb",
  toolBtnBorder: "#e2e2e2",
  toolBtnText: "#4a4e57",
  toolBtnActiveBg: "rgba(0,184,241,0.18)",
  toolBtnActiveBorder: "#00b8f1",
  toolBtnActiveText: "#0084ad",
  plotBg: "#ffffff",
  plotBorder: "#cfd3d8",
  plotAxis: "#4a4e57",
  plotGrid: "#e6e6e6",
};

// Chromalloy Dark: brand cyan accent on dark slate surfaces (chromalloy.com palette).
const chromalloyDark: UiThemeTokens = {
  font: "'Open Sans', 'Segoe UI Variable', 'Segoe UI', sans-serif",
  colorScheme: "dark",
  bgPrimary: "#1a2028",
  bgSecondary: "#273a43",
  overlayDim: "rgba(0,0,0,0.55)",
  surface: "#273a43",
  surfaceBlur: "none",
  surfaceBorder: "1px solid #3a4854",
  surfaceRadius: "8px",
  surfaceShadow: "0 14px 40px rgba(0,0,0,0.55)",
  titleBg: "#212934",
  titleText: "#ffffff",
  titleBorder: "3px solid #00b8f1",
  textPrimary: "#e3e7eb",
  textSecondary: "#9fb3bf",
  textMuted: "#6b7681",
  accent: "#00b8f1",
  accentHover: "#0091c0",
  accentText: "#212934",
  card: "#2f4450",
  cardBorder: "1px solid #3a4854",
  cardRadius: "6px",
  controlBg: "#212934",
  controlBorder: "1px solid #3f4e5a",
  controlRadius: "4px",
  controlHoverBg: "rgba(255,255,255,0.08)",
  tableBg: "#273a43",
  tableBorder: "1px solid #3a4854",
  tableHeaderBg: "#212934",
  tableHeaderText: "#9fb3bf",
  rowDivider: "1px solid #2f3b46",
  selection: "#00b8f1",
  selectionText: "#212934",
  rowHover: "rgba(0,184,241,0.15)",
  footerBg: "#212934",
  footerBorder: "1px solid #3a4854",
  separator: "#3a4854",
  menuBg: "#273a43",
  menuBorder: "1px solid #3a4854",
  menuShadow: "0 12px 30px rgba(0,0,0,0.55)",
  menuBarHover: "rgba(0,184,241,0.22)",
  menuBarHoverText: "#00b8f1",
  btnPrimaryBg: "#00b8f1",
  btnPrimaryHoverBg: "#0091c0",
  btnPrimaryText: "#212934",
  btnPrimaryBorder: "none",
  btnPrimaryShadow: "none",
  btnSecondaryBg: "transparent",
  btnSecondaryHoverBg: "rgba(255,255,255,0.06)",
  btnSecondaryText: "#e3e7eb",
  btnSecondaryBorder: "1px solid #5a6873",
  buttonActive: "rgba(0,184,241,0.3)",
  groupLabelBg: "#273a43",
  treeToggle: "#6b7681",
  panelBg: "#212934",
  toolbarBg: "#212934",
  toolbarBorder: "#343e4a",
  toolBtnBg: "#273a43",
  toolBtnBorder: "#3a4854",
  toolBtnText: "#e3e7eb",
  toolBtnActiveBg: "rgba(0,184,241,0.22)",
  toolBtnActiveBorder: "#00b8f1",
  toolBtnActiveText: "#00b8f1",
  plotBg: "#1a2028",
  plotBorder: "#3a4854",
  plotAxis: "#e3e7eb",
  plotGrid: "#2f3b46",
};

/** Offered under File > Theme (Eric's pick of the June mockups plus the two
 *  Chromalloy brand themes), alphabetical.
 *  Acrylic Glass is the default. */
export const UI_THEMES: readonly UiTheme[] = [
  { id: "acrylic-glass", label: "Acrylic Glass", tokens: acrylicGlass },
  { id: "chromalloy-dark", label: "Chromalloy Dark", tokens: chromalloyDark },
  { id: "chromalloy-light", label: "Chromalloy Light", tokens: chromalloyLight },
  { id: "classic-desktop", label: "Classic Desktop", tokens: classicDesktop },
  { id: "cupertino-light", label: "Cupertino Light", tokens: cupertinoLight },
  { id: "fluent-light", label: "Fluent Light", tokens: fluentLight },
  { id: "high-contrast", label: "High Contrast", tokens: highContrast },
  { id: "industrial-dark", label: "Industrial Dark", tokens: industrialDark },
  { id: "material-you", label: "Material You", tokens: materialYou },
  { id: "nord-twilight", label: "Nord Twilight", tokens: nordTwilight },
  { id: "steel-enterprise", label: "Steel Enterprise", tokens: steelEnterprise },
];

export function isUiThemeId(id: unknown): id is UiThemeId {
  return typeof id === "string" && UI_THEMES.some((t) => t.id === id);
}

/** The theme for `id`, falling back to the default for unknown/missing ids. */
export function getUiTheme(id: string | null | undefined): UiTheme {
  return UI_THEMES.find((t) => t.id === id) ?? UI_THEMES.find((t) => t.id === DEFAULT_UI_THEME)!;
}

/** `--ui-<kebab-case>` custom property name for a token key. */
export function uiCssVarName(key: keyof UiThemeTokens): string {
  return `--ui-${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}`;
}

/** `var(--ui-…)` reference for use in inline styles. */
export function uiVar(key: keyof UiThemeTokens): string {
  return `var(${uiCssVarName(key)})`;
}

/** Write every token of `id` onto `root` (default <html>) as `--ui-*` custom
 *  properties, and stamp `data-ui-theme` + `color-scheme` so native controls
 *  (scrollbars, select popups) match. */
export function applyUiTheme(id: UiThemeId, root?: HTMLElement): void {
  const el = root ?? (typeof document !== "undefined" ? document.documentElement : null);
  if (!el) return;
  const theme = getUiTheme(id);
  for (const [key, value] of Object.entries(theme.tokens)) {
    el.style.setProperty(uiCssVarName(key as keyof UiThemeTokens), value);
  }
  el.style.colorScheme = theme.tokens.colorScheme;
  el.dataset.uiTheme = theme.id;
}

/** The persisted choice, or the default when unset / unknown / storage unavailable. */
export function readStoredUiTheme(): UiThemeId {
  try {
    return getUiTheme(localStorage.getItem(UI_THEME_STORAGE_KEY)).id;
  } catch {
    return DEFAULT_UI_THEME;
  }
}

/** The theme currently applied to this document (falls back to the stored one). */
export function currentUiTheme(): UiThemeId {
  if (typeof document !== "undefined") {
    const applied = document.documentElement.dataset.uiTheme;
    if (isUiThemeId(applied)) return applied;
  }
  return readStoredUiTheme();
}

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/** Mirror `id` into localStorage (best effort). */
function storeLocal(id: UiThemeId): void {
  try {
    localStorage.setItem(UI_THEME_STORAGE_KEY, id);
  } catch {
    // Private mode / storage disabled: the theme still applies for this session.
  }
}

/** Read the theme saved in the user's Tiger prefs ini via the backend
 *  (`get_ui_theme` command / `/api/get_ui_theme`). `null` when unset or the
 *  backend is unreachable (browser tests, dev server without a backend). */
export async function fetchStoredUiTheme(): Promise<UiThemeId | null> {
  try {
    let raw: string | null;
    if (isTauri()) {
      const { invoke } = await import("@tauri-apps/api/core");
      raw = await invoke<string | null>("get_ui_theme");
    } else {
      const r = await fetch("/api/get_ui_theme");
      if (!r.ok) return null;
      raw = (await r.json()) as string | null;
    }
    return isUiThemeId(raw) ? raw : null;
  } catch {
    return null;
  }
}

/** Save `id` to the user's Tiger prefs ini via the backend (fire-and-forget). */
function persistUiTheme(id: UiThemeId): void {
  void (async () => {
    try {
      if (isTauri()) {
        const { invoke } = await import("@tauri-apps/api/core");
        await invoke("set_ui_theme", { name: id });
      } else {
        await fetch(`/api/set_ui_theme?name=${encodeURIComponent(id)}`);
      }
    } catch (e) {
      console.warn("failed to save UI theme to user prefs", e);
    }
  })();
}

/** User picked a theme: persist it (prefs ini + localStorage mirror), apply it
 *  here, and notify this document's React subscribers. Other same-origin
 *  windows pick it up via `storage`. */
export function setUiTheme(id: UiThemeId): void {
  storeLocal(id);
  persistUiTheme(id);
  applyUiTheme(id);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent<UiThemeId>(UI_THEME_EVENT, { detail: id }));
  }
}

/** Entry-point hook: apply the locally mirrored theme now (no flash), then
 *  reconcile with the user's prefs ini via the backend, and follow changes made
 *  in any other window of this origin. Call once per document before first render. */
export function installUiTheme(): void {
  if (typeof window === "undefined") return;
  applyUiTheme(readStoredUiTheme());
  void fetchStoredUiTheme().then((saved) => {
    if (saved && saved !== currentUiTheme()) {
      storeLocal(saved);
      applyUiTheme(saved);
      window.dispatchEvent(new CustomEvent<UiThemeId>(UI_THEME_EVENT, { detail: saved }));
    }
  });
  window.addEventListener("storage", (e) => {
    if (e.key !== UI_THEME_STORAGE_KEY) return;
    const id = getUiTheme(e.newValue).id;
    applyUiTheme(id);
    window.dispatchEvent(new CustomEvent<UiThemeId>(UI_THEME_EVENT, { detail: id }));
  });
}

/** The computed value of a token on this document (e.g. for canvas drawing,
 *  which can't use `var()`), or `fallback` when unavailable. */
export function readUiVar(key: keyof UiThemeTokens, fallback: string): string {
  if (typeof document === "undefined" || typeof getComputedStyle !== "function") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(uiCssVarName(key)).trim();
  return v || fallback;
}

/** Run `cb` whenever the theme changes in this document (own pick or another
 *  window's). For canvas consumers that must repaint — uPlot charts. */
export function useOnUiThemeChange(cb: () => void): void {
  useEffect(() => {
    const onChange = () => cb();
    window.addEventListener(UI_THEME_EVENT, onChange);
    return () => window.removeEventListener(UI_THEME_EVENT, onChange);
  }, [cb]);
}

/** The active theme id, re-rendering on change (menu dot, etc.). */
export function useUiTheme(): UiThemeId {
  const [id, setId] = useState<UiThemeId>(() => currentUiTheme());
  useEffect(() => {
    const onChange = (e: Event) => setId(getUiTheme((e as CustomEvent<string>).detail).id);
    window.addEventListener(UI_THEME_EVENT, onChange);
    return () => window.removeEventListener(UI_THEME_EVENT, onChange);
  }, []);
  return id;
}
